import { TreeNode } from '../models/TreeNode.js';

/**
 * JSONTreeParser converts JSON data into a hierarchical tree structure
 */
export class JSONTreeParser {
  /**
   * Parse JSON data into a tree structure
   * @param {any} jsonData - The parsed JSON data
   * @param {string} rootKey - The key for the root node (default: 'root')
   * @returns {TreeNode} The root node of the tree
   */
  static parse(jsonData, rootKey = 'root') {
    const rootType = JSONTreeParser.getValueType(jsonData);
    const rootNode = new TreeNode(rootKey, jsonData, rootType, [], 0);

    if (rootType === 'object' || rootType === 'array') {
      JSONTreeParser.parseChildren(jsonData, rootNode, [rootKey], 1);
    }

    return rootNode;
  }

  /**
   * Parse JSON data asynchronously with progress callback
   * @param {any} jsonData - The parsed JSON data
   * @param {string} rootKey - The key for the root node (default: 'root')
   * @param {Function} progressCallback - Optional callback for progress updates
   * @returns {Promise<TreeNode>} Promise that resolves to the root node of the tree
   */
  static async parseAsync(jsonData, rootKey = 'root', progressCallback = null) {
    return new Promise((resolve, reject) => {
      try {
        const rootType = JSONTreeParser.getValueType(jsonData);
        const rootNode = new TreeNode(rootKey, jsonData, rootType, [], 0);

        if (rootType === 'object' || rootType === 'array') {
          // Count total items for progress tracking
          const totalItems = JSONTreeParser.countTotalItems(jsonData);
          let processedItems = 0;

          const parseWithProgress = async () => {
            await JSONTreeParser.parseChildrenAsync(
              jsonData,
              rootNode,
              [rootKey],
              1,
              totalItems,
              (processed) => {
                processedItems = processed;
                if (progressCallback) {
                  progressCallback(Math.min(100, Math.round((processed / totalItems) * 100)));
                }
              }
            );

            resolve(rootNode);
          };

          parseWithProgress().catch(reject);
        } else {
          resolve(rootNode);
        }
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Count total items in JSON data for progress tracking
   * @param {any} data - The data to count
   * @returns {number} Total number of items
   */
  static countTotalItems(data) {
    let count = 0;

    const countRecursive = (obj) => {
      if (Array.isArray(obj)) {
        count += obj.length;
        obj.forEach(item => {
          if (typeof item === 'object' && item !== null) {
            countRecursive(item);
          }
        });
      } else if (obj && typeof obj === 'object') {
        const keys = Object.keys(obj);
        count += keys.length;
        keys.forEach(key => {
          const value = obj[key];
          if (typeof value === 'object' && value !== null) {
            countRecursive(value);
          }
        });
      }
    };

    countRecursive(data);
    return Math.max(1, count); // Ensure at least 1 to avoid division by zero
  }

  /**
   * Parse children of a container node (object or array)
   * @param {any} data - The data to parse
   * @param {TreeNode} parentNode - The parent node to add children to
   * @param {string[]} currentPath - The current path in the JSON structure
   * @param {number} level - The current depth level
   */
  static parseChildren(data, parentNode, currentPath, level) {
    if (Array.isArray(data)) {
      // Handle array
      data.forEach((item, index) => {
        const key = `[${index}]`;
        const path = [...currentPath, key];
        const type = JSONTreeParser.getValueType(item);
        const childNode = new TreeNode(key, item, type, path, level);

        parentNode.addChild(childNode);

        // Recursively parse if the item is a container
        if (type === 'object' || type === 'array') {
          JSONTreeParser.parseChildren(item, childNode, path, level + 1);
        }
      });
    } else if (data && typeof data === 'object') {
      // Handle object
      Object.keys(data).forEach(key => {
        const value = data[key];
        const path = [...currentPath, key];
        const type = JSONTreeParser.getValueType(value);
        const childNode = new TreeNode(key, value, type, path, level);

        parentNode.addChild(childNode);

        // Recursively parse if the value is a container
        if (type === 'object' || type === 'array') {
          JSONTreeParser.parseChildren(value, childNode, path, level + 1);
        }
      });
    }
  }

  /**
   * Parse children asynchronously with progress tracking
   * @param {any} data - The data to parse
   * @param {TreeNode} parentNode - The parent node to add children to
   * @param {string[]} currentPath - The current path in the JSON structure
   * @param {number} level - The current depth level
   * @param {number} totalItems - Total items for progress calculation
   * @param {Function} progressCallback - Callback for progress updates
   * @param {Object} context - Shared context for tracking progress
   * @returns {Promise<void>}
   */
  static async parseChildrenAsync(data, parentNode, currentPath, level, totalItems, progressCallback, context = { processed: 0 }) {
    const BATCH_SIZE = 50; // Process items in batches to avoid blocking UI

    if (Array.isArray(data)) {
      // Handle array
      for (let i = 0; i < data.length; i += BATCH_SIZE) {
        const batch = data.slice(i, i + BATCH_SIZE);

        for (let j = 0; j < batch.length; j++) {
          const item = batch[j];
          const index = i + j;
          const key = `[${index}]`;
          const path = [...currentPath, key];
          const type = JSONTreeParser.getValueType(item);
          const childNode = new TreeNode(key, item, type, path, level);

          parentNode.addChild(childNode);
          context.processed++;

          // Recursively parse if the item is a container
          if (type === 'object' || type === 'array') {
            await JSONTreeParser.parseChildrenAsync(item, childNode, path, level + 1, totalItems, progressCallback, context);
          }
        }

        // Update progress and yield control to avoid blocking UI
        if (progressCallback) {
          progressCallback(context.processed);
        }
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    } else if (data && typeof data === 'object') {
      // Handle object
      const keys = Object.keys(data);

      for (let i = 0; i < keys.length; i += BATCH_SIZE) {
        const batchKeys = keys.slice(i, i + BATCH_SIZE);

        for (const key of batchKeys) {
          const value = data[key];
          const path = [...currentPath, key];
          const type = JSONTreeParser.getValueType(value);
          const childNode = new TreeNode(key, value, type, path, level);

          parentNode.addChild(childNode);
          context.processed++;

          // Recursively parse if the value is a container
          if (type === 'object' || type === 'array') {
            await JSONTreeParser.parseChildrenAsync(value, childNode, path, level + 1, totalItems, progressCallback, context);
          }
        }

        // Update progress and yield control to avoid blocking UI
        if (progressCallback) {
          progressCallback(context.processed);
        }
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }
  }

  /**
   * Determine the type of a JSON value
   * @param {any} value - The value to check
   * @returns {string} The type ('object', 'array', 'string', 'number', 'boolean', 'null')
   */
  static getValueType(value) {
    if (value === null) {
      return 'null';
    }
    if (Array.isArray(value)) {
      return 'array';
    }
    if (typeof value === 'object') {
      return 'object';
    }
    if (typeof value === 'string') {
      return 'string';
    }
    if (typeof value === 'number') {
      return 'number';
    }
    if (typeof value === 'boolean') {
      return 'boolean';
    }
    // Fallback for any other types
    return 'unknown';
  }

  /**
   * Find a node by its path
   * @param {TreeNode} rootNode - The root node to search from
   * @param {string[]} targetPath - The path to search for
   * @returns {TreeNode|null} The found node or null if not found
   */
  static findNodeByPath(rootNode, targetPath) {
    if (targetPath.length === 0) {
      return rootNode;
    }

    // Remove the root key from the target path if it matches
    const searchPath = targetPath[0] === rootNode.key ? targetPath.slice(1) : targetPath;

    return JSONTreeParser.searchNode(rootNode, searchPath, 0);
  }

  /**
   * Recursively search for a node by path
   * @param {TreeNode} currentNode - The current node being searched
   * @param {string[]} searchPath - The remaining path to search
   * @param {number} pathIndex - The current index in the search path
   * @returns {TreeNode|null} The found node or null if not found
   */
  static searchNode(currentNode, searchPath, pathIndex) {
    if (pathIndex >= searchPath.length) {
      return currentNode;
    }

    const targetKey = searchPath[pathIndex];
    const childNode = currentNode.children.find(child => child.key === targetKey);

    if (!childNode) {
      return null;
    }

    return JSONTreeParser.searchNode(childNode, searchPath, pathIndex + 1);
  }

  /**
   * Get all leaf nodes (nodes without children) from the tree
   * @param {TreeNode} rootNode - The root node to start from
   * @returns {TreeNode[]} Array of leaf nodes
   */
  static getLeafNodes(rootNode) {
    const leafNodes = [];

    function collectLeafNodes(node) {
      if (!node.hasChildren()) {
        leafNodes.push(node);
      } else {
        node.children.forEach(child => collectLeafNodes(child));
      }
    }

    collectLeafNodes(rootNode);
    return leafNodes;
  }

  /**
   * Get all nodes at a specific level
   * @param {TreeNode} rootNode - The root node to start from
   * @param {number} targetLevel - The level to collect nodes from
   * @returns {TreeNode[]} Array of nodes at the specified level
   */
  static getNodesAtLevel(rootNode, targetLevel) {
    const nodesAtLevel = [];

    function collectNodesAtLevel(node) {
      if (node.level === targetLevel) {
        nodesAtLevel.push(node);
      }
      node.children.forEach(child => collectNodesAtLevel(child));
    }

    collectNodesAtLevel(rootNode);
    return nodesAtLevel;
  }

  /**
   * Expand all nodes up to a specific level
   * @param {TreeNode} rootNode - The root node to start from
   * @param {number} maxLevel - The maximum level to expand to
   */
  static expandToLevel(rootNode, maxLevel) {
    function expandNodes(node) {
      if (node.level < maxLevel) {
        node.isExpanded = true;
        node.children.forEach(child => expandNodes(child));
      } else {
        node.isExpanded = false;
      }
    }

    expandNodes(rootNode);
  }
}